import { copy, type ArcadeId } from './catalog';
import { colleague, cup, face, rounded } from './drawing';
import { aimedVelocity, invasionPressure } from './invaders-logic';
import { DIRECTIONS, equal, freeCell, MAZE, mazeStep, pathDirection, snakeStep, type Cell } from './logic';

export interface Hooks { points(n: number): void; say(it: string, en: string): void; hit(): void; finish(won: boolean, it: string, en: string): void; sound(kind: 'eat' | 'hit' | 'shoot' | 'power'): void }
export interface Engine { update(dt: number): void; draw(c: CanvasRenderingContext2D, time: number): void; direction(d: Cell): void; fire(held: boolean): void; pointer?(x: number): void; progress(): string }

export function createEngine(id: ArcadeId, h: Hooks): Engine {
  return id === 'espresso' ? invaders(h) : id === 'office' ? office(h) : snake(h);
}

function snake(h: Hooks): Engine {
  let body: Cell[] = [{ x: 7, y: 8 }, { x: 6, y: 8 }, { x: 5, y: 8 }, { x: 4, y: 8 }];
  let dir = { x: 1, y: 0 }, pending = dir, queued = false;
  let food = freeCell(body)!, bonus: Cell | null = null, bonusKind = 'chilli', bonusLife = 0;
  let timer = 0, eaten = 0, spice = 0, ended = false;
  return {
    direction(d) { if (!queued && !(d.x === -dir.x && d.y === -dir.y)) { pending = d; queued = true; } },
    fire() {},
    progress: () => `${eaten}/20 ${copy('snack', 'snacks')}${spice > 0 ? ' · 🌶 TURBO' : ''}`,
    update(dt) {
      if (ended) return;
      spice = Math.max(0, spice - dt); bonusLife -= dt; if (bonusLife <= 0) bonus = null;
      timer += dt;
      const interval = spice > 0 ? .085 : Math.max(.115, .205 - eaten * .004);
      if (timer < interval) return;
      timer -= interval; dir = pending; queued = false;
      const nextHead = { x: (body[0].x + dir.x + 20) % 20, y: (body[0].y + dir.y + 16) % 16 };
      const eating = equal(nextHead, food), special = bonus && equal(nextHead, bonus);
      const next = snakeStep(body, dir, eating);
      if (!next) { ended = true; h.finish(false, 'Hai assaggiato te stesso. Recensione: troppo gommoso.', 'You tasted yourself. Review: too chewy.'); return; }
      body = next;
      if (special) {
        if (bonusKind === 'chilli') { spice = 4; h.points(35); h.say('PICANTE! Anche il serpente ha chiesto acqua.', 'SPICY! Even the snake asked for water.'); }
        else { body = body.slice(0, Math.max(4, body.length - 3)); h.points(20); h.say('Limone detox. La coda ha dato le dimissioni.', 'Lemon detox. Your tail just resigned.'); }
        bonus = null; h.sound('power');
      }
      if (eating) {
        eaten++; h.points(10 + Math.floor(eaten / 5) * 5); h.sound('eat');
        if (eaten >= 20) { ended = true; h.finish(true, 'Aperitivo completato. Ora serve un mutuo.', 'Happy hour complete. Now you need a mortgage.'); return; }
        food = freeCell(body)!;
        if (eaten % 3 === 0) {
          bonusKind = eaten % 6 === 0 ? 'lemon' : 'chilli'; bonus = freeCell([...body, food]); bonusLife = 7;
        }
        if (eaten % 5 === 0) h.say(`Il conto è salito a €${eaten * 3}. «Dividiamo alla romana?»`, `The bill is €${eaten * 3}. “Shall we split it?”`);
      }
    },
    draw(c, time) {
      c.strokeStyle = '#ffffff06'; c.lineWidth = 1;
      for (let x = 0; x <= 600; x += 30) { c.beginPath(); c.moveTo(x, 0); c.lineTo(x, 480); c.stroke(); }
      for (let y = 0; y <= 480; y += 30) { c.beginPath(); c.moveTo(0, y); c.lineTo(600, y); c.stroke(); }
      body.slice().reverse().forEach((p, i) => rounded(c, p.x * 30 + 2, p.y * 30 + 2, 26, 26, 8, spice > 0 ? '#ffab6e' : i === body.length - 1 ? '#d5fa86' : '#89aa51'));
      face(c, body[0].x * 30 + 15, body[0].y * 30 + 15, 26, spice > 0 ? '#ffab6e' : '#d5fa86');
      c.font = '25px sans-serif'; c.textAlign = 'center'; c.textBaseline = 'middle';
      c.fillText(['🫒', '🧀', '🥨', '🍕'][eaten % 4], food.x * 30 + 15, food.y * 30 + 15 + Math.sin(time * 5) * 2);
      if (bonus) c.fillText(bonusKind === 'chilli' ? '🌶️' : '🍋', bonus.x * 30 + 15, bonus.y * 30 + 15);
    },
  };
}

function office(h: Hooks): Engine {
  const spawn = { x: 9, y: 11 };
  let player = { ...spawn }, dir = { x: 0, y: 0 }, pending = dir;
  const starts = [{ x: 1, y: 1 }, { x: 17, y: 1 }, { x: 9, y: 3 }];
  const colleagues = starts.map((p, role) => ({ ...p, role }));
  const pellets = new Set<string>(), coffees = new Set(['1,13', '17,13', '1,3', '17,3']);
  MAZE.forEach((row, y) => [...row].forEach((v, x) => { if (v === '.' && !equal({ x, y }, spawn)) pellets.add(`${x},${y}`); }));
  const total = pellets.size;
  let timer = 0, colleagueTimer = 0, power = 0, lives = 3, immune = 1.5, ended = false, combo = 0;
  function collision(): void {
    for (let i = 0; i < colleagues.length; i++) {
      if (!equal(player, colleagues[i])) continue;
      if (power > 0) {
        Object.assign(colleagues[i], starts[i]); h.points(100 * ++combo); h.sound('power');
        h.say('«Sono in pausa». Il collega è tornato alla scrivania.', '“I’m on break.” Your colleague went back to their desk.');
      } else if (immune <= 0) {
        lives--; h.hit();
        if (!lives) { ended = true; h.finish(false, '«Hai un minuto?» È durato tre ore.', '“Got a minute?” It lasted three hours.'); return; }
        player = { ...spawn }; dir = pending = { x: 0, y: 0 }; immune = 2;
        colleagues.forEach((g, n) => Object.assign(g, starts[n]));
        h.say(['Il capo: «Hai un minuto?». Ti ha sequestrato un’ora.', 'La HR: «Solo una firma». Il modulo ha 47 pagine.', 'Il collega: «Ti aggiungo alla call». Addio pausa.'][i], ['The boss: “Got a minute?” An hour has vanished.', 'HR: “Just a signature.” The form is 47 pages long.', 'Your colleague: “I’ll add you to the call.” Goodbye break.'][i]); break;
      }
    }
  }
  return {
    direction(d) { pending = d; }, fire() {},
    progress: () => `${total - pellets.size}/${total} snack · ${'♥'.repeat(lives)}${power > 0 ? ' · ☕' : ''}`,
    update(dt) {
      if (ended) return;
      timer += dt; colleagueTimer += dt; power = Math.max(0, power - dt); immune -= dt;
      if (timer >= .13) {
        timer -= .13;
        if (mazeStep(player, pending)) dir = pending;
        const next = mazeStep(player, dir); if (next) player = next;
        const key = `${player.x},${player.y}`;
        if (pellets.delete(key)) { h.points(10); h.sound('eat'); }
        if (coffees.delete(key)) { power = 7; combo = 0; h.points(40); h.sound('power'); h.say('PAUSA CAFFÈ. Ora i colleghi scappano da te.', 'COFFEE BREAK. Now your colleagues run away from you.'); }
        collision();
        if (!pellets.size && !ended) { ended = true; h.finish(true, 'Tutti gli snack spariti. Il vero benefit aziendale.', 'All snacks gone. The real employee benefit.'); }
      }
      if (colleagueTimer >= (power > 0 ? .32 : .27)) {
        colleagueTimer = 0;
        colleagues.forEach((g, i) => {
          let d: Cell;
          if (power > 0) {
            const options = DIRECTIONS.filter((v) => mazeStep(g, v));
            options.sort((a, b) => {
              const p = mazeStep(g, a)!, q = mazeStep(g, b)!;
              return (Math.abs(q.x - player.x) + Math.abs(q.y - player.y)) - (Math.abs(p.x - player.x) + Math.abs(p.y - player.y));
            });
            d = options[0];
          } else if (i === 2 && Math.random() < .45) {
            const options = DIRECTIONS.filter((v) => mazeStep(g, v)); d = options[Math.floor(Math.random() * options.length)];
          } else d = pathDirection(g, player);
          const next = mazeStep(g, d); if (next) Object.assign(g, next);
        });
        collision();
      }
    },
    draw(c, time) {
      const ox = 15, oy = 15, s = 30;
      MAZE.forEach((row, y) => [...row].forEach((v, x) => {
        if (v === '#') { rounded(c, ox + x * s + 2, oy + y * s + 2, 26, 26, 5, '#514e3b'); rounded(c, ox + x * s + 5, oy + y * s + 4, 20, 3, 1, '#6f6b50'); }
      }));
      pellets.forEach((key) => {
        const [x, y] = key.split(',').map(Number);
        if (coffees.has(key)) cup(c, ox + x * s + 15, oy + y * s + 15, 22);
        else { c.fillStyle = '#eaca8f'; c.beginPath(); c.arc(ox + x * s + 15, oy + y * s + 15, 3, 0, 7); c.fill(); }
      });
      if (immune <= 0 || Math.sin(time * 20) > 0) {
        const angle = Math.atan2(dir.y, dir.x), mouth = .2 + Math.abs(Math.sin(time * 12)) * .4;
        c.fillStyle = '#ffd17f'; c.beginPath(); c.moveTo(ox + player.x * s + 15, oy + player.y * s + 15); c.arc(ox + player.x * s + 15, oy + player.y * s + 15, 12, angle + mouth, angle + Math.PI * 2 - mouth); c.fill();
      }
      colleagues.forEach((g) => colleague(c, ox + g.x * s + 15, oy + g.y * s + 15, 27, g.role, power > 0, time));
    },
  };
}

function invaders(h: Hooks): Engine {
  type Enemy = { x: number; y: number; hp: number; maxHp: number; kind: number };
  type Shot = { x: number; y: number; vx: number };
  type Threat = Shot & { vy: number; boss: boolean };
  let enemies: Enemy[] = [], shots: Shot[] = [], threats: Threat[] = [];
  let x = 300, movement = 0, firing = false, cooldown = 0, wave = 1, march = 1, lives = 3, immune = 0, power = 0;
  let attack = .7, ended = false, drop: { x: number; y: number } | null = null, kills = 0, formationSize = 0, volley = 0;
  let bossAttack = 1.2, warning: { enemy: Enemy; targetX: number; remaining: number } | null = null;
  function spawn(): void {
    enemies = [];
    for (let row = 0; row < 4; row++) for (let col = 0; col < 8; col++) {
      const hp = (wave === 3 && row < 2) || (wave === 2 && row === 1) ? 2 : 1;
      enemies.push({ x: 90 + col * 56, y: 54 + row * 46, hp, maxHp: hp, kind: row % 3 });
    }
    if (wave === 3) enemies.push({ x: 300, y: 22, hp: 26, maxHp: 26, kind: 3 });
    formationSize = enemies.length; march = 1; attack = .7; bossAttack = 1.2; warning = null;
  }
  function damage(): void {
    if (immune > 0 || ended) return;
    lives--; immune = .85; h.hit();
    if (!lives) { ended = true; h.finish(false, 'La call ha vinto. Poteva essere una mail.', 'The call won. It could have been an email.'); }
    else h.say('Notifica ricevuta. Autostima danneggiata.', 'Notification received. Self-esteem damaged.');
  }
  spawn();
  return {
    direction(d) { movement = d.x; }, fire(held) { firing = held; }, pointer(px) { x = Math.max(24, Math.min(576, px)); },
    progress: () => `HARD · ${copy('Ondata', 'Wave')} ${wave}/3 · ${'♥'.repeat(lives)}${power > 0 ? ' · ×3 ☕' : ''}`,
    update(dt) {
      if (ended) return;
      x = Math.max(24, Math.min(576, x + movement * dt * 340));
      cooldown -= dt; immune -= dt; power = Math.max(0, power - dt); attack -= dt;
      const pressure = invasionPressure(wave, enemies.length, formationSize);
      if (firing && cooldown <= 0) {
        cooldown = power > 0 ? .13 : .22;
        shots.push({ x, y: 427, vx: 0 });
        if (power > 0) shots.push({ x: x - 13, y: 427, vx: -50 }, { x: x + 13, y: 427, vx: 50 });
        h.sound('shoot');
      }
      let edge = false;
      enemies.forEach((e) => {
        e.x += march * dt * pressure.marchSpeed;
        const margin = e.kind === 3 ? 52 : 28;
        if (e.x < margin || e.x > 600 - margin) edge = true;
      });
      if (edge) {
        march *= -1;
        enemies.forEach((e) => { const margin = e.kind === 3 ? 52 : 28; e.y += pressure.descent; e.x = Math.max(margin, Math.min(600 - margin, e.x)); });
      }
      if (enemies.some((e) => e.y > 404)) { ended = true; h.finish(false, 'Il lunedì è atterrato. Nessun caffè può salvarci.', 'Monday has landed. No coffee can save us.'); return; }
      if (attack <= 0 && enemies.length) {
        attack = pressure.attackInterval; volley++;
        // Fire from the front line so incoming notifications remain visible.
        const front = enemies.filter((e) => e.kind !== 3 && !enemies.some((other) => other.kind !== 3 && Math.abs(other.x - e.x) < 5 && other.y > e.y));
        const pool = front.length ? front : enemies;
        const first = Math.floor(Math.random() * pool.length);
        for (let i = 0; i < Math.min(pressure.shooters, pool.length); i++) {
          const enemy = pool[(first + i * Math.max(1, Math.floor(pool.length / pressure.shooters))) % pool.length];
          const origin = { x: enemy.x, y: enemy.y + 16 };
          const velocity = (volley + i) % 2 === 0 ? aimedVelocity(origin, x, pressure.projectileSpeed) : { vx: 0, vy: pressure.projectileSpeed };
          threats.push({ ...origin, ...velocity, boss: false });
        }
      }
      const boss = enemies.find((e) => e.kind === 3);
      if (boss) {
        bossAttack -= dt;
        if (!warning && bossAttack <= 0) { warning = { enemy: boss, targetX: x, remaining: .75 }; bossAttack = 2.4; }
      }
      if (warning) {
        warning.remaining -= dt;
        if (warning.enemy.hp <= 0) warning = null;
        else if (warning.remaining <= 0) {
          const origin = { x: warning.enemy.x, y: warning.enemy.y + 18 };
          for (const angle of [-.48, -.24, 0, .24, .48]) threats.push({ ...origin, ...aimedVelocity(origin, warning.targetX, 315, angle), boss: true });
          warning = null;
        }
      }
      for (const shot of shots) {
        shot.y -= 440 * dt; shot.x += shot.vx * dt;
        const e = enemies.find((enemy) => Math.abs(enemy.x - shot.x) < (enemy.kind === 3 ? 47 : 19) && Math.abs(enemy.y - shot.y) < 20);
        if (e) {
          shot.y = -100; e.hp--;
          if (e.hp <= 0) {
            enemies = enemies.filter((v) => v !== e); kills++; h.points(e.kind === 3 ? 500 : 25); h.sound('eat');
            if (kills % 9 === 0) drop = { x: e.x, y: e.y };
            if (kills % 8 === 0) h.say(['Riunione cancellata. La produttività ringrazia.', 'Inbox zero. Per circa 0,3 secondi.', '«Facciamo un giro di tavolo». Tavolo distrutto.'][Math.floor(kills / 8) % 3], ['Meeting cancelled. Productivity thanks you.', 'Inbox zero. For about 0.3 seconds.', '“Let’s go around the table.” Table destroyed.'][Math.floor(kills / 8) % 3]);
          }
        }
      }
      shots = shots.filter((s) => s.y > -20);
      threats.forEach((s) => {
        s.y += s.vy * dt; s.x += s.vx * dt;
        if (Math.abs(s.x - x) < 23 && Math.abs(s.y - 441) < 18) { s.y = 600; damage(); }
      }); threats = threats.filter((s) => s.y < 510 && s.x > -25 && s.x < 625);
      if (drop) {
        drop.y += dt * 120;
        if (Math.abs(drop.x - x) < 30 && Math.abs(drop.y - 441) < 25) { drop = null; power = 4.5; h.sound('power'); h.say('DOPPIO ESPRESSO. Ora sei legalmente un razzo.', 'DOUBLE ESPRESSO. You are legally a rocket now.'); }
        else if (drop.y > 510) drop = null;
      }
      if (!enemies.length && !ended) {
        if (wave === 3) { ended = true; h.finish(true, 'Hai sconfitto il lunedì. Martedì vuole parlarti.', 'You defeated Monday. Tuesday wants a word.'); }
        else { wave++; threats = []; spawn(); h.say(wave === 3 ? 'BOSS: «Una call veloce». 26 punti di ego. Occhio a «Rispondi a tutti»!' : 'Seconda ondata. Email corazzate. Il lunedì fa gli straordinari.', wave === 3 ? 'BOSS: “A quick call”. 26 ego points. Watch out for “Reply all”!' : 'Second wave. Armoured emails. Monday is working overtime.'); }
      }
    },
    draw(c, time) {
      c.fillStyle = '#ffffff25';
      for (let i = 0; i < 45; i++) c.fillRect((i * 137) % 600, (i * 71 + time * (7 + i % 4)) % 480, 2, 2);
      enemies.forEach((e) => {
        if (e.kind === 3) {
          rounded(c, e.x - 49, e.y - 17, 98, 33, 9, warning ? '#ff8395' : '#f4b277');
          c.fillStyle = '#302a48'; c.font = 'bold 10px monospace'; c.textAlign = 'center'; c.fillText(copy('CALL VELOCE', 'QUICK CALL'), e.x, e.y + 4);
          rounded(c, e.x - 44, e.y + 19, 88, 4, 2, '#4f3a49'); rounded(c, e.x - 44, e.y + 19, 88 * e.hp / e.maxHp, 4, 2, '#ffcfb1');
        }
        else { face(c, e.x, e.y, 32, ['#f4b277', '#bdaaff', '#9ecacb'][e.kind], 'angry'); c.fillStyle = '#fff4df'; if (e.kind === 0) { c.fillRect(e.x - 8, e.y - 22, 16, 4); } else if (e.kind === 1) { c.strokeStyle = '#302a48'; c.lineWidth = 2; c.strokeRect(e.x - 10, e.y - 9, 20, 15); } }
        if (e.kind !== 3 && e.hp > 1) { c.strokeStyle = '#fff1c8'; c.lineWidth = 2; c.strokeRect(e.x - 19, e.y - 19, 38, 38); }
      });
      shots.forEach((s) => rounded(c, s.x - 2, s.y, 5, 15, 2, '#c9f36b'));
      threats.forEach((s) => { c.fillStyle = s.boss ? '#ffca85' : '#ee97ae'; c.font = s.boss ? 'bold 20px monospace' : 'bold 18px monospace'; c.textAlign = 'center'; c.fillText(s.boss ? '@' : '!', s.x, s.y); });
      if (warning) {
        c.strokeStyle = '#ff8395'; c.lineWidth = 2; c.setLineDash([5, 5]);
        c.beginPath(); c.moveTo(warning.enemy.x, warning.enemy.y + 25); c.lineTo(warning.targetX, 441); c.stroke(); c.setLineDash([]);
        rounded(c, 148, 389, 304, 24, 8, '#543041'); c.fillStyle = '#ffc2ca'; c.font = 'bold 11px monospace'; c.textAlign = 'center';
        c.fillText(copy('⚠ RISPOSTA A TUTTI · SPOSTATI!', '⚠ REPLY ALL · MOVE!'), 300, 406);
      }
      if (drop) { rounded(c, drop.x - 17, drop.y - 17, 34, 34, 17, '#c9f36b'); c.fillStyle = '#23352e'; c.font = 'bold 17px monospace'; c.textAlign = 'center'; c.fillText('×3', drop.x, drop.y + 5); }
      if (immune <= 0 || Math.sin(time * 22) > 0) cup(c, x, 442, 43);
    },
  };
}
